'use client';

import React, { useState } from 'react';
import HeroSection from './HeroSection';
import InputSection from './InputSection';
import AnalysisResults from './AnalysisResults';
import AnalysisLoadingSkeleton from './AnalysisLoadingSkeleton';
import { toast } from 'sonner';

export interface AnalysisResult {
  matchScore: number;
  callbackPotential: 'Low' | 'Medium' | 'High';
  strongMatches: string[];
  missingSkillsOrKeywords: string[];
  weakAreas: string[];
  suggestedImprovements: string[];
  tailoredBulletRewrites: { originalIdea: string; improvedVersion: string }[];
  finalAssessment: string;
}

type AppState = 'idle' | 'analyzing' | 'results' | 'error';

const VALIDATION_ERROR =
  'We need readable text from both the resume and the job description before we can analyze the match.';

function isMeaningfulContent(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length < 50) return false;
  const wordCount = trimmed.split(/\s+/).filter((w) => w.length > 1).length;
  return wordCount >= 5;
}

export default function AnalysisTool() {
  const [appState, setAppState] = useState<AppState>('idle');
  const [jobDescription, setJobDescription] = useState('');
  const [resume, setResume] = useState('');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleAnalyze = async () => {
    if (!isMeaningfulContent(jobDescription)) {
      toast.error(VALIDATION_ERROR);
      return;
    }

    if (!isMeaningfulContent(resume)) {
      toast.error(VALIDATION_ERROR);
      return;
    }

    setAppState('analyzing');
    setErrorMessage('');

    try {
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resumeText: resume.trim(),
          jobDescriptionText: jobDescription.trim(),
        }),
      });

      const raw = await response.text();

    let data: any = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch {
      data = { error: raw || 'Server returned invalid JSON' };
    }

    if (!response.ok) {
      const msg =
        data?.details ||
        data?.error ||
        raw ||
        'Analysis failed. Please try again.';
      setAppState('error');
      setErrorMessage(msg);
      toast.error(data?.error || 'Analysis failed.');
      return;
    }

      setResult(data as AnalysisResult);
      setAppState('results');
      toast.success('Analysis complete — your report is ready.');
    } catch (err: any) {
      const msg =
        err?.message || 'Analysis failed. Check your connection and try again.';
      setAppState('error');
      setErrorMessage(msg);
      toast.error(msg);
    }
  };

  const handleReset = () => {
    setAppState('idle');
    setResult(null);
    setJobDescription('');
    setResume('');
    setErrorMessage('');
  };

  const handleReanalyze = () => {
    setAppState('idle');
    setResult(null);
    setErrorMessage('');
  };

  return (
    <div data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:112:4" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="112" data-component-end-line="197" data-component-file="AnalysisTool.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20gap-10%22%7D" className="flex flex-col gap-10" suppressHydrationWarning>
      {appState === 'idle' && (
        <>
          <HeroSection data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:115:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="115" data-component-end-line="115" data-component-file="AnalysisTool.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D" />
          <InputSection data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:116:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="116" data-component-end-line="123" data-component-file="AnalysisTool.tsx" data-component-name="InputSection" data-component-content="%7B%22elementName%22%3A%22InputSection%22%7D"
            jobDescription={jobDescription}
            resume={resume}
            onJobDescriptionChange={setJobDescription}
            onResumeChange={setResume}
            onAnalyze={handleAnalyze}
            isAnalyzing={false}
          />
        </>
      )}

      {appState === 'analyzing' && (
        <>
          <HeroSection data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:129:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="129" data-component-end-line="129" data-component-file="AnalysisTool.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D" compact />
          <InputSection data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:130:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="130" data-component-end-line="138" data-component-file="AnalysisTool.tsx" data-component-name="InputSection" data-component-content="%7B%22elementName%22%3A%22InputSection%22%7D"
            jobDescription={jobDescription}
            resume={resume}
            onJobDescriptionChange={setJobDescription}
            onResumeChange={setResume}
            onAnalyze={handleAnalyze}
            isAnalyzing={true}
            disabled
          />
          <AnalysisLoadingSkeleton data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:139:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="139" data-component-end-line="139" data-component-file="AnalysisTool.tsx" data-component-name="AnalysisLoadingSkeleton" data-component-content="%7B%22elementName%22%3A%22AnalysisLoadingSkeleton%22%7D" />
        </>
      )}

      {appState === 'results' && result && (
        <>
          <div data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:145:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="145" data-component-end-line="168" data-component-file="AnalysisTool.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20flex-wrap%20gap-4%22%7D" className="flex items-center justify-between flex-wrap gap-4">
            <div data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:146:12" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="146" data-component-end-line="151" data-component-file="AnalysisTool.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h2 data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:147:14" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="147" data-component-end-line="147" data-component-file="AnalysisTool.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-white%22%2C%22textContent%22%3A%22Analysis%20Report%22%7D" className="text-2xl font-bold text-white">Analysis Report</h2>
              <p data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:148:14" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="148" data-component-end-line="150" data-component-file="AnalysisTool.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-zinc-400%20mt-1%22%2C%22textContent%22%3A%22Based%20on%20your%20resume%20vs.%20the%20provided%20job%20description%22%7D" className="text-sm text-zinc-400 mt-1">
                Based on your resume vs. the provided job description
              </p>
            </div>

            <div data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:153:12" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="153" data-component-end-line="167" data-component-file="AnalysisTool.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">
              <button data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:154:14" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="154" data-component-end-line="159" data-component-file="AnalysisTool.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-2%20rounded-lg%20border%20border-zinc-700%20text-zinc-300%20text-sm%20font-medium%20hover%3Abg-zinc-800%20hover%3Atext-white%20transition-all%20duration-150%20active%3Ascale-95%22%2C%22textContent%22%3A%22Edit%20Inputs%22%7D"
                onClick={handleReanalyze}
                className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 text-sm font-medium hover:bg-zinc-800 hover:text-white transition-all duration-150 active:scale-95"
              >
                Edit Inputs
              </button>

              <button data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:161:14" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="161" data-component-end-line="166" data-component-file="AnalysisTool.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-2%20rounded-lg%20bg-indigo-600%20hover%3Abg-indigo-500%20text-white%20text-sm%20font-semibold%20transition-all%20duration-150%20active%3Ascale-95%22%2C%22textContent%22%3A%22Start%20New%20Analysis%22%7D"
                onClick={handleReset}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition-all duration-150 active:scale-95"
              >
                Start New Analysis
              </button>
            </div>
          </div>

          <AnalysisResults data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:170:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="170" data-component-end-line="170" data-component-file="AnalysisTool.tsx" data-component-name="AnalysisResults" data-component-content="%7B%22elementName%22%3A%22AnalysisResults%22%7D" result={result} />
        </>
      )}

      {appState === 'error' && (
        <div data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:175:8" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="175" data-component-end-line="195" data-component-file="AnalysisTool.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20justify-center%20py-20%20gap-6%22%7D" className="flex flex-col items-center justify-center py-20 gap-6">
          <div data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:176:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="176" data-component-end-line="178" data-component-file="AnalysisTool.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20rounded-full%20bg-rose-500%2F10%20border%20border-rose-500%2F20%20flex%20items-center%20justify-center%22%7D" className="w-16 h-16 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center">
            <span data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:177:12" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="177" data-component-end-line="177" data-component-file="AnalysisTool.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xl%22%2C%22textContent%22%3A%22%E2%9A%A0%EF%B8%8F%22%7D" className="text-2xl">⚠️</span>
          </div>

          <div data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:180:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="180" data-component-end-line="187" data-component-file="AnalysisTool.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
            <h3 data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:181:12" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="181" data-component-end-line="183" data-component-file="AnalysisTool.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-white%20mb-2%22%2C%22textContent%22%3A%22Analysis%20Failed%22%7D" className="text-lg font-semibold text-white mb-2">
              Analysis Failed
            </h3>
            <p data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:184:12" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="184" data-component-end-line="186" data-component-file="AnalysisTool.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-zinc-400%20text-sm%20max-w-md%20whitespace-pre-wrap%20break-words%22%7D" className="text-zinc-400 text-sm max-w-md whitespace-pre-wrap break-words">
              {errorMessage}
            </p>
          </div>

          <button data-component-id="src/app/resume-analysis-tool/components/AnalysisTool.tsx:189:10" data-component-path="src/app/resume-analysis-tool/components/AnalysisTool.tsx" data-component-line="189" data-component-end-line="194" data-component-file="AnalysisTool.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-5%20py-2.5%20rounded-lg%20bg-indigo-600%20hover%3Abg-indigo-500%20text-white%20font-semibold%20text-sm%20transition-all%20duration-150%20active%3Ascale-95%22%2C%22textContent%22%3A%22Try%20Again%22%7D"
            onClick={() => setAppState('idle')}
            className="px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-all duration-150 active:scale-95"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}